import { AppStore } from '../state/store';
import { setClosed } from '../state/commands';
import { autoBankAt, rollAt } from '../core/track/roll';
import { hasForcedPitch, hasForcedYaw, gravityBankDefinable, yawPitchFromTan } from '../core/track/orientation';
import { displayBankDeg } from '../core/track/flyBank';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';

export { yawPitchFromTan };

/**
 * Right-hand Track panel: selected node position, yaw/pitch/bank (optional
 * overrides), and auto readouts.
 */
export class TrackPanel {
  private el: HTMLElement;
  private applying = false;

  constructor(private store: AppStore) {
    this.el = document.createElement('div');
    sidebarPane('build').appendChild(this.el);

    store.on('selection', () => this.render());
    store.on('doc', () => this.render());
    store.on('mode', () => this.render());
    store.on('track', () => {
      if (this.applying) {
        this.syncLiveValues();
        return;
      }
      this.render();
    });
    this.render();
  }

  private render(): void {
    if (this.store.mode !== 'build') {
      this.el.innerHTML = '';
      return;
    }

    const idx = this.store.selectedIndices();
    let html = panelStart('track', 'Track');
    html += this.closedCircuitRow();
    html += this.hotkeyRows();

    if (idx.length === 0) {
      html += `<div class="row"><label>Select a track node to edit position and orientation.</label></div>`;
      html += panelEnd();
      this.el.innerHTML = html;
      bindPanelCollapse(this.el);
      this.wireClosed();
      return;
    }

    if (idx.length > 1) {
      html += `<div class="row"><label>${idx.length} nodes selected — pick one for track details.</label></div>`;
      html += panelEnd();
      this.el.innerHTML = html;
      bindPanelCollapse(this.el);
      this.wireClosed();
      return;
    }

    const i = idx[0];
    const node = this.store.doc.track.nodes[i];
    const info = this.nodeInfo(i);
    const yaw = Math.round(info.yaw);
    const pitch = Math.round(info.pitch);
    const bank = Math.round(info.bank);
    const forcedPos = !!node.forcedPos;
    const forcedBank = node.forcedBankDeg !== undefined;
    const bankLocked = !info.canBank;

    html += `<div class="row"><label>Node ${i}</label></div>`;

    html += `<div class="row override-row">
      <label class="override-toggle"><input type="checkbox" data-key="overridePos"${
        forcedPos ? ' checked' : ''
      }> Override XYZ</label>
      <div class="override-controls">`;
    for (const axis of ['x', 'y', 'z'] as const) {
      html += `<label>${axis.toUpperCase()}</label>
        <input type="number" step="0.5" data-axis="${axis}" value="${node.p[axis].toFixed(2)}"${
          forcedPos ? '' : ' disabled'
        }>`;
    }
    html += `</div></div>`;

    html += `<div class="row override-row">
      <label class="override-toggle"><input type="checkbox" data-key="overrideBank"${
        forcedBank ? ' checked' : ''
      }${bankLocked ? ' disabled' : ''}> Override banking</label>
      <div class="override-controls">
        <input type="range" data-key="bank" min="-360" max="360" step="1" value="${bank}"${
          bankLocked ? ' disabled' : ''
        }>
        <span class="val" data-read="bank">${fmtDeg(info.bank)}</span>
      </div>
    </div>`;
    html += `<div class="row"><label>Banking (auto)</label><span class="val" data-read="autoBank">${fmtDeg(info.autoBank)}</span></div>`;
    if (bankLocked) {
      html += `<div class="hint">Banking cannot be overridden while the track is vertical — 0° is rails level with the ground, which is not defined on a vertical tangent.</div>`;
    }

    html += `<div class="row override-row">
      <label class="override-toggle"><input type="checkbox" data-key="overrideYaw"${
        hasForcedYaw(node) ? ' checked' : ''
      }> Override yaw</label>
      <div class="override-controls">
        <input type="range" data-key="yaw" min="-180" max="180" step="1" value="${yaw}">
        <span class="val" data-read="yaw">${fmtDeg(info.yaw)}</span>
      </div>
    </div>`;
    html += `<div class="row"><label>Yaw (auto)</label><span class="val" data-read="autoYaw">${fmtDeg(info.autoYaw)}</span></div>`;

    html += `<div class="row override-row">
      <label class="override-toggle"><input type="checkbox" data-key="overridePitch"${
        hasForcedPitch(node) ? ' checked' : ''
      }> Override pitch</label>
      <div class="override-controls">
        <input type="range" data-key="pitch" min="-90" max="90" step="1" value="${pitch}">
        <span class="val" data-read="pitch">${fmtDeg(info.pitch)}</span>
      </div>
    </div>`;
    html += `<div class="row"><label>Pitch (auto)</label><span class="val" data-read="autoPitch">${fmtDeg(info.autoPitch)}</span></div>`;

    html += `<div class="hint">Track banking follows the rails (RMF). Override banking is gravity-relative: 0° means rails level with the ground. That angle is saved on this node and kept if earlier pieces change; it is converted into track-relative roll when the rails are built. Between override nodes the track takes the shortest turn (never more than 180°), so a 360° twist needs three pins. Sliders follow the spline until their override is on. Moving the node enables Override XYZ.</div>`;
    html += panelEnd();

    this.el.innerHTML = html;
    bindPanelCollapse(this.el);
    this.wireClosed();
    this.wire(i);
  }

  private closedCircuitRow(): string {
    const n = this.store.doc.track.nodes.length;
    const closed = this.store.doc.track.closed;
    const canClose = n >= 3;
    const disabled = canClose ? '' : ' disabled';
    const checked = closed ? ' checked' : '';
    return `<div class="row"><label><input type="checkbox" data-action="closed"${checked}${disabled}> Connect end to start</label></div>
      <div class="hint">${canClose ? 'Closes the circuit so the last node joins the first.' : 'Needs at least 3 nodes to close the circuit.'}</div>`;
  }

  private hotkeyRows(): string {
    return `<div class="hotkeys">
      <div class="hotkey-row"><kbd>Insert</kbd><span>Add a straight piece at the end</span></div>
      <div class="hotkey-row"><kbd>Delete</kbd><span>Remove the selected piece</span></div>
      <div class="hotkey-row"><kbd>Home</kbd><span>Next piece</span></div>
      <div class="hotkey-row"><kbd>End</kbd><span>Previous piece</span></div>
    </div>`;
  }

  private wireClosed(): void {
    this.el.querySelector<HTMLInputElement>('input[data-action="closed"]')?.addEventListener('change', (e) => {
      setClosed(this.store, (e.target as HTMLInputElement).checked);
    });
  }

  /** Update numeric readouts / sliders without rebuilding (keeps drag focus). */
  private syncLiveValues(): void {
    const idx = this.store.selectedIndices();
    if (idx.length !== 1) return;
    const i = idx[0];
    const node = this.store.doc.track.nodes[i];
    const info = this.nodeInfo(i);
    const forcedPos = !!node.forcedPos;

    setText(this.el, 'yaw', fmtDeg(info.yaw));
    setText(this.el, 'pitch', fmtDeg(info.pitch));
    setText(this.el, 'autoYaw', fmtDeg(info.autoYaw));
    setText(this.el, 'autoPitch', fmtDeg(info.autoPitch));
    setText(this.el, 'bank', fmtDeg(info.bank));
    setText(this.el, 'autoBank', fmtDeg(info.autoBank));

    for (const key of ['yaw', 'pitch', 'bank'] as const) {
      const slider = this.el.querySelector<HTMLInputElement>(`input[data-key="${key}"]`);
      if (slider && document.activeElement !== slider) {
        slider.value = String(Math.round(info[key]));
      }
    }
    for (const axis of ['x', 'y', 'z'] as const) {
      const input = this.el.querySelector<HTMLInputElement>(`input[data-axis="${axis}"]`);
      if (input && document.activeElement !== input) {
        input.value = node.p[axis].toFixed(2);
        input.disabled = !forcedPos;
      }
    }
    const posBox = this.el.querySelector<HTMLInputElement>('input[data-key="overridePos"]');
    if (posBox) posBox.checked = forcedPos;
    const bankBox = this.el.querySelector<HTMLInputElement>('input[data-key="overrideBank"]');
    if (bankBox) {
      bankBox.checked = node.forcedBankDeg !== undefined;
      bankBox.disabled = !info.canBank;
    }
    const bankSlider = this.el.querySelector<HTMLInputElement>('input[data-key="bank"]');
    if (bankSlider) bankSlider.disabled = !info.canBank;
    const yawBox = this.el.querySelector<HTMLInputElement>('input[data-key="overrideYaw"]');
    if (yawBox) yawBox.checked = hasForcedYaw(node);
    const pitchBox = this.el.querySelector<HTMLInputElement>('input[data-key="overridePitch"]');
    if (pitchBox) pitchBox.checked = hasForcedPitch(node);
  }

  private nodeInfo(i: number): {
    yaw: number;
    pitch: number;
    autoYaw: number;
    autoPitch: number;
    bank: number;
    autoBank: number;
    canBank: boolean;
  } {
    const node = this.store.doc.track.nodes[i];
    const cl = this.store.runtime.centerline;
    const s = cl.spanStartS(i);
    const tan = cl.tanAt(s);
    const { yaw, pitch } = yawPitchFromTan(tan);
    const cached = this.store.autoOrientationById.get(node.id);
    const autoYaw = cached?.yaw ?? yaw;
    const autoPitch = cached?.pitch ?? pitch;
    const L = this.store.runtime.totalLength;
    const closed = this.store.doc.track.closed;
    const keys = this.store.doc.rollKeys;
    const authored = rollAt(keys, s, L, closed);
    const autoAuthored = autoBankAt(keys, s, L, closed);
    const frame = this.store.runtime.frameAt(s);
    const canBank = gravityBankDefinable(tan);
    // Pinned override is stored gravity-relative so earlier-piece RMF drift
    // cannot change the number the user set.
    const bank =
      canBank && node.forcedBankDeg !== undefined
        ? node.forcedBankDeg
        : displayBankDeg(frame, authored);
    const autoBank = displayBankDeg(frame, autoAuthored);
    return { yaw, pitch, autoYaw, autoPitch, bank, autoBank, canBank };
  }

  private wire(nodeIndex: number): void {
    this.el.querySelectorAll<HTMLInputElement>('input[data-axis]').forEach((input) => {
      input.addEventListener('change', () => {
        const axis = input.dataset.axis as 'x' | 'y' | 'z';
        const value = parseFloat(input.value);
        if (!isFinite(value)) return;
        this.store.commit((d) => {
          const n = d.track.nodes[nodeIndex];
          n.p[axis] = value;
          n.forcedPos = true;
        }, [nodeIndex]);
      });
    });

    this.el.querySelector<HTMLInputElement>('input[data-key="overridePos"]')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      this.store.commit((d) => {
        const n = d.track.nodes[nodeIndex];
        if (on) n.forcedPos = true;
        else delete n.forcedPos;
      }, [nodeIndex]);
    });

    this.el.querySelector<HTMLInputElement>('input[data-key="overrideBank"]')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      const info = this.nodeInfo(nodeIndex);
      this.store.commit((d) => {
        const n = d.track.nodes[nodeIndex];
        if (on) {
          if (!info.canBank) return;
          n.forcedBankDeg = Math.round(info.autoBank);
        } else delete n.forcedBankDeg;
      }, [nodeIndex]);
    });

    this.el.querySelector<HTMLInputElement>('input[data-key="overrideYaw"]')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      const info = this.nodeInfo(nodeIndex);
      this.store.commit((d) => {
        const n = d.track.nodes[nodeIndex];
        if (on) n.forcedYawDeg = Math.round(info.autoYaw);
        else delete n.forcedYawDeg;
      }, [nodeIndex]);
    });

    this.el.querySelector<HTMLInputElement>('input[data-key="overridePitch"]')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      const info = this.nodeInfo(nodeIndex);
      this.store.commit((d) => {
        const n = d.track.nodes[nodeIndex];
        if (on) n.forcedPitchDeg = Math.round(info.autoPitch);
        else delete n.forcedPitchDeg;
      }, [nodeIndex]);
    });

    const bankSlider = this.el.querySelector<HTMLInputElement>('input[data-key="bank"]');
    bankSlider?.addEventListener('input', () => {
      const displayDeg = parseFloat(bankSlider.value);
      if (!isFinite(displayDeg)) return;
      const node = this.store.doc.track.nodes[nodeIndex];
      const cl = this.store.runtime.centerline;
      const s = cl.spanStartS(nodeIndex);
      const frame = this.store.runtime.frameAt(s);
      if (!gravityBankDefinable(frame.tan)) return;
      this.store.beginGesture();
      this.applying = true;
      node.forcedBankDeg = Math.round(displayDeg);
      this.store.updateGesture([nodeIndex]);
      this.applying = false;
      setText(this.el, 'bank', fmtDeg(displayDeg));
      const box = this.el.querySelector<HTMLInputElement>('input[data-key="overrideBank"]');
      if (box) box.checked = true;
    });
    bankSlider?.addEventListener('change', () => this.store.endGesture());

    for (const key of ['yaw', 'pitch'] as const) {
      const input = this.el.querySelector<HTMLInputElement>(`input[data-key="${key}"]`);
      if (!input) continue;
      input.addEventListener('input', () => {
        const deg = parseFloat(input.value);
        if (!isFinite(deg)) return;
        const node = this.store.doc.track.nodes[nodeIndex];
        this.store.beginGesture();
        this.applying = true;
        if (key === 'yaw') node.forcedYawDeg = deg;
        else node.forcedPitchDeg = deg;
        this.store.updateGesture([nodeIndex]);
        this.applying = false;
        setText(this.el, key, fmtDeg(deg));
        const boxKey = key === 'yaw' ? 'overrideYaw' : 'overridePitch';
        const box = this.el.querySelector<HTMLInputElement>(`input[data-key="${boxKey}"]`);
        if (box) box.checked = true;
      });
      input.addEventListener('change', () => this.store.endGesture());
    }
  }
}

function fmtDeg(deg: number): string {
  const rounded = Math.round(deg * 10) / 10;
  return `${rounded}°`;
}

function setText(root: HTMLElement, key: string, text: string): void {
  const el = root.querySelector(`[data-read="${key}"]`);
  if (el) el.textContent = text;
}
